import "../index.css";
import { useEffect } from "react";
import Footer from "../components/Footer";
//import { useAuth } from "../auth/context";
import { PageNav } from "../components/PageNav";
import ReactMarkdown from "react-markdown";

export default function Terms() {
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const markdownContent = `
# Terms of Service

**Last Updated: October 25, 2025**

## 1. Agreement to Terms

Welcome to StakeAlerts! These Terms of Service ("Terms") govern your access to and use of the StakeAlerts mobile application and website (collectively, the "Service") operated by StakeAlerts LLC ("StakeAlerts," "we," "us," or "our").

**By accessing or using our Service, you agree to be bound by these Terms.** If you do not agree to these Terms, do not use our Service.

## 2. Description of Service

StakeAlerts is a sports betting information and alert service that:
- Aggregates and displays real-time odds and betting lines from various sportsbooks (including DraftKings, FanDuel, and others)
- Allows you to create custom alert rules based on specific conditions (e.g., sports, teams, betting markets, odds thresholds)
- Sends notifications (push notifications and SMS) when betting opportunities match your criteria
- Provides live game monitoring and odds comparison

**Important: StakeAlerts is NOT a gambling site.** We do not:
- Accept or process bets
- Hold or transfer money for gambling purposes
- Operate as a sportsbook or betting platform
- Guarantee any betting outcomes
- Provide betting advice or recommendations

We provide information only. All betting decisions and activities are solely your responsibility.

## 3. Eligibility and Age Requirements

### 3.1 Age Requirement

You must be at least **18 years old** (or **21 years old** in jurisdictions where that is the minimum age for accessing sports betting information) to use our Service.

By using our Service, you represent and warrant that:
- You meet the minimum age requirement
- You have the legal capacity to enter into these Terms
- You are not prohibited from using the Service under applicable laws

### 3.2 Geographic Availability

Our Service is available throughout the United States. Since we do not process bets or operate as a sportsbook, we simply provide information about publicly available odds and betting lines.

**Your Responsibility:** You are responsible for ensuring that:
- Your use of sports betting information complies with your local and state laws
- Any betting activities you choose to engage in (with third-party sportsbooks) comply with applicable gambling laws in your jurisdiction
- You understand the legal status of sports betting in your state

**Note:** While we provide information nationwide, the legality of sports betting itself varies by state. We recommend you understand your local laws before placing any bets with third-party sportsbooks.

## 4. Account Registration and Security

### 4.1 Account Creation

To use our Service, you must create an account using one of the following methods:
- Email and password
- Google Sign-In
- Apple Sign-In

You agree to:
- Provide accurate and complete information
- Keep your account information up to date
- Maintain the security of your account credentials
- Notify us immediately of any unauthorized access

### 4.2 Account Responsibility

You are solely responsible for:
- All activities that occur under your account
- Maintaining the confidentiality of your account credentials
- Any actions taken using your account, whether authorized by you or not

We are not liable for any loss or damage arising from unauthorized use of your account.

### 4.3 Account Termination

You may terminate your account at any time by:
- Canceling your subscription through the Service
- Contacting us at support@stakealerts.com

We may suspend or terminate your account if:
- You violate these Terms
- You engage in fraudulent or illegal activity
- You abuse the Service or harass other users or our staff
- Your payment fails or your account is delinquent
- We discontinue the Service

## 5. Subscription and Payment Terms

### 5.1 Paid Subscription

StakeAlerts is a subscription-based service. Access to the Service requires an active paid subscription.

**Payment Methods:**
- Web subscriptions: Processed through Stripe
- iOS subscriptions: Processed through Apple App Store
- Android subscriptions: Processed through Google Play Store

### 5.2 Subscription Fees

- Subscription fees are clearly displayed before you subscribe
- Fees are billed in advance on a recurring basis (monthly, annually, or as otherwise specified)
- All fees are in U.S. Dollars (USD) unless otherwise stated
- Fees are non-refundable except as required by law or as stated in Section 5.6

### 5.3 Automatic Renewal

Your subscription will automatically renew at the end of each billing period unless you cancel before the renewal date.

**To cancel:**
- Web subscriptions: Cancel through your account settings or contact support@stakealerts.com
- iOS subscriptions: Cancel through your Apple App Store account settings
- Android subscriptions: Cancel through your Google Play Store account settings

You must cancel at least 24 hours before your renewal date to avoid being charged for the next period.

### 5.4 Price Changes

We may change subscription prices at any time. Price changes will:
- Be communicated to you at least 30 days in advance
- Not affect your current billing period
- Apply to subsequent billing periods after notice
- Give you the option to cancel before the new price takes effect

### 5.5 Free Trials

We may offer free trials from time to time. Free trial terms will be specified at the time of offer. If you do not cancel before the free trial ends, you will be charged the applicable subscription fee.

### 5.6 Refunds

**General Policy:** All subscription fees are non-refundable except:
- Where required by applicable law
- If we terminate your account without cause
- In cases of billing errors

**Requesting a Refund:** Contact support@stakealerts.com within 7 days of the charge. We will review refund requests on a case-by-case basis.

**App Store Purchases:** For subscriptions purchased through Apple App Store or Google Play Store, refunds are subject to their respective refund policies, not ours.

### 5.7 Taxes

You are responsible for any applicable taxes related to your subscription. If we are required to collect taxes, they will be added to your subscription fee.

## 6. Acceptable Use Policy

### 6.1 Permitted Use

You may use the Service only for:
- Personal, non-commercial purposes
- Lawful purposes in compliance with these Terms and all applicable laws
- Viewing sports betting information and receiving alerts

### 6.2 Prohibited Activities

You agree NOT to:

**Account Misuse:**
- Share your account credentials with others
- Create multiple accounts for the same individual
- Use another person's account without permission
- Circumvent any access restrictions or bypass payment requirements

**Service Abuse:**
- Use the Service in any way that could damage, disable, or impair our servers or networks
- Attempt to gain unauthorized access to any part of the Service
- Use automated tools (bots, scrapers) to access the Service without our written permission
- Reverse engineer, decompile, or disassemble any part of the Service
- Remove or modify any copyright, trademark, or proprietary notices

**Illegal or Harmful Activities:**
- Use the Service for any illegal purpose or in violation of any laws
- Use the Service if you are located in a jurisdiction where sports betting information is prohibited
- Transmit any viruses, malware, or harmful code
- Harass, abuse, or harm other users or our staff
- Impersonate any person or entity

**Data Misuse:**
- Scrape, copy, or redistribute odds data for commercial purposes
- Create derivative products or services using our data
- Sell or license access to the Service

### 6.3 Enforcement

Violation of this Acceptable Use Policy may result in:
- Warning or suspension of your account
- Immediate termination of your account without refund
- Legal action if your conduct violates applicable laws

## 7. Odds Data and Information Accuracy

### 7.1 No Guarantee of Accuracy

While we strive to provide accurate and timely information:
- Odds and betting lines are provided "as is"
- We do not guarantee the accuracy, completeness, or timeliness of any information
- Odds can change rapidly and may be outdated by the time you receive them
- Technical issues may cause delays or errors in data delivery

### 7.2 Third-Party Data

We obtain odds and betting information from third-party sportsbooks. We are not responsible for:
- Errors in data provided by sportsbooks
- Changes in sportsbook offerings or availability
- Sportsbook terms, conditions, or policies

### 7.3 Alerts and Notifications

- Alerts are sent on a best-effort basis
- We do not guarantee that you will receive every alert
- Network conditions, device settings, or technical issues may prevent or delay alerts
- You are responsible for configuring your device to receive notifications

### 7.4 Not Financial or Betting Advice

**StakeAlerts does not provide betting advice, recommendations, or guaranteed outcomes.** All information is for informational purposes only. You are solely responsible for:
- Your betting decisions
- Understanding the risks of gambling
- Complying with gambling laws in your jurisdiction
- Any financial losses from betting

## 8. Intellectual Property Rights

### 8.1 Our Intellectual Property

The Service and its original content, features, and functionality are owned by StakeAlerts LLC and are protected by:
- Copyright laws
- Trademark laws
- Patent laws
- Trade secret laws
- Other intellectual property laws

Our trademarks, logos, and service marks ("Marks") may not be used without our prior written permission.

### 8.2 Limited License

We grant you a limited, non-exclusive, non-transferable, revocable license to:
- Access and use the Service for personal, non-commercial purposes
- View odds information and receive alerts as intended

This license does not include the right to:
- Copy, modify, or create derivative works
- Distribute, sell, or license the Service or its content
- Use the Service for commercial purposes
- Extract data for use outside the Service

### 8.3 User Content

You retain ownership of any content you submit to the Service (such as alert rules and preferences). By using the Service, you grant us a license to use this content solely to provide the Service to you.

### 8.4 Feedback

If you provide feedback, suggestions, or ideas about the Service, we may use them without any obligation to compensate you.

## 9. Third-Party Services and Links

### 9.1 Third-Party Authentication

If you sign in using Google Sign-In or Apple Sign-In, you agree to their respective terms of service and privacy policies.

### 9.2 Payment Processors

Payments are processed by:
- Stripe (for web subscriptions)
- Apple App Store (for iOS subscriptions)
- Google Play Store (for Android subscriptions)

Your payment information is subject to their terms and privacy policies.

### 9.3 Sportsbook Information

We display information from third-party sportsbooks but have no affiliation, partnership, or business relationship with them. If you choose to visit a sportsbook and place bets:
- You do so at your own risk
- You are subject to their terms and conditions
- We are not responsible for their actions or your interactions with them

### 9.4 No Endorsement

Displaying information from a sportsbook does not constitute an endorsement or recommendation of that sportsbook.

## 10. Disclaimers and Limitations of Liability

### 10.1 Service "As Is"

THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
- Warranties of merchantability
- Fitness for a particular purpose
- Non-infringement
- Accuracy or reliability of information
- Uninterrupted or error-free operation

### 10.2 No Gambling Guarantees

WE MAKE NO REPRESENTATIONS OR WARRANTIES REGARDING:
- The accuracy of odds or betting information
- The availability of betting opportunities
- Any potential winnings or outcomes from betting
- The legality of gambling in your jurisdiction

### 10.3 Limitation of Liability

TO THE MAXIMUM EXTENT PERMITTED BY LAW, STAKEALERTS LLC, ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR:

**Indirect Damages:**
- Indirect, incidental, special, consequential, or punitive damages
- Loss of profits, revenue, data, or business opportunities
- Cost of substitute services

**Direct Damages:**
- Any damages exceeding the amount you paid to StakeAlerts in the 12 months preceding the claim

**Gambling Losses:**
- Any gambling losses or financial losses from betting decisions
- Losses resulting from delayed, inaccurate, or missed alerts
- Losses from reliance on information provided by the Service

### 10.4 Basis of the Bargain

These limitations apply even if we have been advised of the possibility of such damages. These limitations are fundamental to the agreement between you and StakeAlerts.

### 10.5 Jurisdictional Limitations

Some jurisdictions do not allow the exclusion or limitation of certain warranties or liabilities. In such cases, our liability will be limited to the maximum extent permitted by law.

## 11. Indemnification

You agree to indemnify, defend, and hold harmless StakeAlerts LLC, its officers, directors, employees, agents, and affiliates from and against any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys' fees) arising from:

- Your use or misuse of the Service
- Your violation of these Terms
- Your violation of any laws or regulations
- Your betting activities or gambling losses
- Your infringement of any third-party rights
- Any content you submit to the Service

This indemnification obligation survives termination of these Terms and your use of the Service.

## 12. Dispute Resolution and Arbitration

### 12.1 Informal Resolution

Before filing a claim, you agree to contact us at support@stakealerts.com to attempt to resolve the dispute informally. We will attempt to resolve the dispute within 60 days.

### 12.2 Binding Arbitration

If we cannot resolve the dispute informally, you and StakeAlerts agree to resolve any disputes through **binding arbitration** rather than in court, except as provided below.

**Arbitration Terms:**
- Arbitration will be conducted by the American Arbitration Association (AAA)
- Arbitration will follow AAA's Consumer Arbitration Rules
- Arbitration will be held in Boulder County, Colorado or remotely if agreed
- The arbitrator's decision is binding and final
- Each party pays their own attorneys' fees unless the arbitrator decides otherwise

### 12.3 Exceptions to Arbitration

You or we may bring a claim in small claims court if it qualifies. Either party may also seek injunctive relief in court for intellectual property infringement.

### 12.4 Class Action Waiver

**YOU AND STAKEALERTS AGREE THAT DISPUTES WILL BE RESOLVED ON AN INDIVIDUAL BASIS ONLY.** You waive any right to participate in a class action lawsuit or class-wide arbitration.

### 12.5 Governing Law

These Terms are governed by the laws of the State of Colorado, United States, without regard to conflict of law principles.

## 13. Modifications to Terms

### 13.1 Right to Modify

We reserve the right to modify these Terms at any time. When we make changes:
- We will update the "Last Updated" date at the top
- We will notify you via email or through the Service
- Material changes will be effective 30 days after notice

### 13.2 Acceptance of Changes

Your continued use of the Service after changes take effect constitutes acceptance of the modified Terms. If you do not agree to the changes, you must stop using the Service and cancel your subscription.

## 14. Service Modifications and Termination

### 14.1 Right to Modify Service

We reserve the right to:
- Modify, suspend, or discontinue any part of the Service
- Change features, functionality, or availability
- Add or remove sportsbook data sources
- Change alert rules or notification methods

We will provide reasonable notice of material changes when possible.

### 14.2 Service Termination

We may terminate or suspend the Service at any time, with or without notice, for any reason including:
- Business reasons
- Legal or regulatory requirements
- Technical limitations

If we permanently discontinue the Service, we will provide prorated refunds for unused subscription time.

## 15. Severability

If any provision of these Terms is found to be invalid or unenforceable, that provision will be limited or eliminated to the minimum extent necessary, and the remaining provisions will remain in full force and effect.

## 16. Entire Agreement

These Terms, together with our Privacy Policy, constitute the entire agreement between you and StakeAlerts regarding the Service and supersede all prior agreements and understandings.

## 17. Waiver

Our failure to enforce any right or provision of these Terms will not be deemed a waiver of such right or provision.

## 18. Assignment

You may not assign or transfer these Terms or your account without our written consent. We may assign these Terms to any affiliate or in connection with a merger, acquisition, or sale of assets.

## 19. Survival

Provisions that by their nature should survive termination will survive, including: ownership provisions, warranty disclaimers, indemnification, limitations of liability, and dispute resolution.

## 20. Accessibility

We strive to make our Service accessible to all users. If you have difficulty accessing any part of the Service, please contact us at support@stakealerts.com.

## 21. Contact Information

For questions, concerns, or notices regarding these Terms, please contact us:

**StakeAlerts LLC**  
Boulder County, Colorado  
Email: support@stakealerts.com

For legal notices, please include "Legal Notice" in the subject line.

## 22. Responsible Gambling Notice

**Gambling involves financial risk and can be addictive.** While StakeAlerts provides information only and does not process bets, we encourage responsible gambling practices:

- Never bet more than you can afford to lose
- Gambling should be for entertainment, not income
- Set limits on your betting activity
- Seek help if gambling becomes a problem

**Resources for Problem Gambling:**
- National Council on Problem Gambling: 1-800-522-4700
- Gamblers Anonymous: www.gamblersanonymous.org

---

**By using StakeAlerts, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.**`;

  return (
    <div>
      <PageNav />
      <div className="main_div">
        <div
          className="terms-container"
          style={{
            maxWidth: "800px",
            margin: "0 auto",
            padding: "2rem",
            lineHeight: "1.6",
          }}
        >
          <ReactMarkdown
            components={{
              h1: ({ ...props }) => (
                <h1
                  className="font_family_base"
                  style={{
                    fontSize: "2.5rem",
                    marginBottom: "1rem",
                    borderBottom: "2px solid #737373",
                    paddingBottom: "0.5rem",
                  }}
                  {...props}
                />
              ),
              h2: ({ ...props }) => (
                <h2
                  className="font_family_base header_text"
                  style={{
                    fontSize: "1.8rem",
                    marginTop: "2rem",
                    marginBottom: "1rem",
                    // color: "#00bf62",
                  }}
                  {...props}
                />
              ),
              h3: ({ ...props }) => (
                <h3
                  className="font_family_base detail_text"
                  style={{
                    fontSize: "1.4rem",
                    marginTop: "1.5rem",
                    marginBottom: "0.75rem",
                  }}
                  {...props}
                />
              ),
              p: ({ ...props }) => (
                <p
                  className="font_family_base detail_text"
                  style={{ marginBottom: "1rem" }}
                  {...props}
                />
              ),
              ul: ({ ...props }) => (
                <ul
                  className="font_family_base detail_text"
                  style={{ marginLeft: "2rem", marginBottom: "1rem" }}
                  {...props}
                />
              ),
              li: ({ ...props }) => (
                <li
                  className="font_family_base detail_text"
                  style={{ marginBottom: "0.5rem" }}
                  {...props}
                />
              ),
              strong: ({ ...props }) => (
                <strong
                  className="font_family_base header_text"
                  // style={{ color: "#00bf62" }}
                  {...props}
                />
              ),
              hr: ({ ...props }) => (
                <hr
                  style={{
                    border: "none",
                    borderTop: "1px solid #737373",
                    margin: "2rem 0",
                  }}
                  {...props}
                />
              ),
            }}
          >
            {markdownContent}
          </ReactMarkdown>
        </div>
      </div>
      <Footer />
    </div>
  );
}
